import { QRCodeSVG } from 'qrcode.react';
import { ReactNode, useEffect } from 'react';

import RoomCode from '~/modules/elements/room-code';
import useQueryParam from '~/modules/hooks/use-query-param';
import RemoteMicServer from '~/modules/remote-mic/network/server';
import buildRoomLink from '~/modules/utils/build-room-link';

interface Props {
  /** The "use this device's mic" action, which has to be registered by the screen that owns it. */
  children?: ReactNode;
  /** A smaller code and tighter band, so two rows of slot cards still fit above it on a 1080 screen. */
  compact?: boolean;
}

/** Everything needed to turn a phone into a microphone, in one band: the code to scan, the address
 * to type, and the room code to type it with. */
export default function JoinPhonePanel({ children, compact = false }: Props) {
  const gameCode = useQueryParam('room') ?? RemoteMicServer.getGameCode();
  const link = buildRoomLink('remote-mic', gameCode);

  useEffect(() => {
    RemoteMicServer.start();
  }, []);

  const shown = `${new URL(link).host}${new URL(link).pathname}`;

  return (
    <div
      className={`flex items-center gap-8 rounded-xl border border-white/10 bg-black/45 px-7 ${compact ? 'py-4' : 'py-6'}`}>
      {/* The thing the panel is for, at a size a phone can read from across the room - at 150px it
          was a thumbnail beside the text. */}
      <div
        className={`flex shrink-0 items-center justify-center rounded-xl bg-white p-[0.625rem] ${
          compact ? 'size-[11rem]' : 'size-[16.5rem]'
        }`}>
        <QRCodeSVG value={link} width="100%" height="100%" marginSize={0} />
      </div>

      <div className="flex flex-1 flex-col gap-1.5">
        <span className="stroke-text typography text-[1.75rem] font-bold">Use a phone as a microphone</span>
        <span className="text-xl text-white/60">
          Scan the code, or open {/* The full room link, shown as the short address it reads as from a sofa. */}
          <a href={link} data-test="server-link" className="font-bold text-white">
            {shown}
          </a>{' '}
          and enter the room code. The phone takes the next free player slot.
        </span>
      </div>

      <div className="flex flex-col items-center gap-1.5 border-l border-white/10 px-7">
        <span className="text-[0.9375rem] tracking-[0.08em] text-white/45 uppercase">Room code</span>
        <RoomCode
          code={gameCode}
          data-test="game-code"
          className="stroke-text text-active text-[3.5rem] leading-none font-bold tabular-nums"
        />
      </div>

      {children}
    </div>
  );
}
